import { useRef, useEffect, useCallback } from "react";
import { Bot } from "lucide-react";
import { theme } from "../../../theme";
import { ChatMessage } from "./ChatMessage";
import { ChatComposer } from "./ChatComposer";
import { EmptyThreadState } from "./EmptyThreadState";
import { ProviderToolbar } from "./ProviderToolbar";

type MessageData = {
  id: string;
  role: "user" | "assistant";
  content: string;
  metadata?: Record<string, unknown>;
};

type ChatThreadProps = {
  messages: MessageData[];
  isRunning: boolean;
  onSend: (content: string) => void;
  showSources: boolean;
  onShowSourcesChange: (show: boolean) => void;
  initialSuggestions: string[];
  isDisabled?: boolean;
  disabledMessage?: string;
};

export const ChatThread = ({
  messages,
  isRunning,
  onSend,
  showSources,
  onShowSourcesChange,
  initialSuggestions,
  isDisabled,
  disabledMessage,
}: ChatThreadProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, scrollToBottom]);

  // Auto-scroll when mobile keyboard opens
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const handleResize = () => scrollToBottom();
    viewport.addEventListener("resize", handleResize);
    return () => viewport.removeEventListener("resize", handleResize);
  }, [scrollToBottom]);

  // Find the last assistant message for follow-up display
  const lastAssistantId = [...messages]
    .reverse()
    .find((m) => m.role === "assistant")?.id;

  // Hide typing dots once streaming tokens are arriving
  const isStreamingContent = messages.some(
    (m) => m.role === "assistant" && m.metadata?.streaming === true && m.content.length > 0
  );

  // Scroll as streaming tokens arrive
  useEffect(() => {
    if (!isStreamingContent) return;
    scrollToBottom();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, isStreamingContent]);

  return (
    <div className={theme.chat.card}>
      {/* Provider toolbar */}
      <ProviderToolbar
        showSources={showSources}
        onShowSourcesChange={onShowSourcesChange}
      />

      {/* Messages area */}
      <div
        ref={scrollContainerRef}
        className={theme.chat.messagesArea}
        role="log"
        aria-label="Chat conversation"
        aria-live="polite"
      >
        <div className="mx-auto space-y-4">
          {messages.length === 0 && (
            <EmptyThreadState
              suggestions={initialSuggestions}
              onSuggestionClick={onSend}
            />
          )}

          {messages.map((message) => {
            // Don't render the streaming placeholder until tokens start arriving
            if (message.role === "assistant" && message.metadata?.streaming === true && message.content.length === 0) {
              return null;
            }

            const meta = message.metadata as {
              sources?: string[] | null;
              rawContent?: string | null;
              skillApplied?: boolean;
              skillName?: string | null;
              followUpQuestions?: string[];
              provider?: string | null;
              error?: string;
            } | undefined;

            return (
              <ChatMessage
                key={message.id}
                id={message.id}
                role={message.role}
                content={message.content}
                metadata={meta}
                showSources={showSources}
                isLastAssistant={message.id === lastAssistantId}
                isRunning={isRunning}
                onFollowUp={onSend}
              />
            );
          })}

          {/* Typing indicator — hide once streaming tokens arrive */}
          {isRunning && !isStreamingContent && (
            <div className="flex justify-start" role="status">
              <div className="flex items-center space-x-3">
                <div className={theme.chat.botAvatar}>
                  <Bot className={`w-4 h-4 ${theme.text.accent}`} />
                </div>
                <div className={theme.chat.aiBubble}>
                  <span className="flex items-center gap-1.5 py-1">
                    <span
                      className={`w-1.5 h-1.5 dark:bg-sy-teal bg-teal-600 rounded-full ${theme.anim.bounce}`}
                      style={{ animationDelay: "0ms" }}
                    />
                    <span
                      className={`w-1.5 h-1.5 dark:bg-sy-teal bg-teal-600 rounded-full ${theme.anim.bounce}`}
                      style={{ animationDelay: "160ms" }}
                    />
                    <span
                      className={`w-1.5 h-1.5 dark:bg-sy-teal bg-teal-600 rounded-full ${theme.anim.bounce}`}
                      style={{ animationDelay: "320ms" }}
                    />
                  </span>
                  <span className="sr-only">Charlie is thinking</span>
                </div>
              </div>
            </div>
          )}

          <div />
        </div>
      </div>

      {/* Composer */}
      <ChatComposer
        onSend={onSend}
        isRunning={isRunning}
        isDisabled={isDisabled}
        disabledMessage={disabledMessage}
      />
    </div>
  );
};
